import BasePage from "pages/base_page"
import React from "react"
import { createRoot } from "react-dom/client"
import ContactAuthorView from "views/contact_author_view"
import ToggleLockUser from "components/Editorial/ToggleLockUser"
import RemoveAllComments from "components/Editorial/CommentModeration/RemoveAllComments"
import escape from "lodash/escape"
import { currentUser } from "lib/currentUser"
import { isSessionActive } from "lib/currentUser/session"
import fetch from "lib/fetch"
import { wrapInUserModel } from "models/user"
import i18n from "lib/i18n"

export default class ProfilePage extends BasePage {
  async initialize(options) {
    super.initialize(options)

    this.activeTab = options.active_tab
    this.profileUserId = options.user_id
    this.profileUserSlug = options.user_slug
    this.profileUserType = options.user_type
    this.profileUserFullName = options.user_full_name
    this.locked = options.locked
    this.contactable = options.contactable
    this.availableFor = options.availableFor
    this.closeable = options.closeable
    this.hasPublicPhones = options.has_public_phones
    this.hasPublicEmails = options.has_public_emails

    this.currentUser = await currentUser().then(wrapInUserModel)

    this.profileActionsElement = this.$el.find(".profile-user-actions")
    this.tabNavigation = this.$el.find(".tabbed-profile-navigation")

    this.renderTabs()
    this.renderProfileActions()
    this.renderContactDetails()
  }

  viewingOwnProfile() {
    return this.currentUser.id === this.profileUserId
  }

  renderTabs() {
    this.renderNewsFeedTab()

    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      this.tabNavigation.removeClass("opacity-0")
    }, 100)
  }

  renderNewsFeedTab() {
    if (!this.viewingOwnProfile()) return

    const newsFeedTab = document.createElement("a")
    newsFeedTab.href = `/profiles/${this.profileUserSlug}/news`
    newsFeedTab.className = `tab news ${this.activeTab === "news" ? "active" : ""}`
    newsFeedTab.textContent = i18n.t("profile.label.news_feed")

    this.tabNavigation.append(newsFeedTab)
  }

  renderProfileActions() {
    this.profileActionsElement.find("svg").fadeOut(200, () => {
      this.renderContactAuthor()
      this.renderDashboardButton()
      this.renderEditProfileButton()
      this.renderModerateCommentsButton()
      this.renderSendBriefButton()
      this.renderBehaviourButton()
      this.renderCloseAccountButton()
      this.renderRemoveAllCommentsButton()
      this.renderToggleLockUserButton()
    })
  }

  createButton(href, text, className = "button large") {
    const button = document.createElement("a")
    button.href = href
    button.className = className
    button.textContent = i18n.t(text)
    return button
  }

  renderContactAuthor() {
    if (this.viewingOwnProfile()) return
    if (this.profileUserType !== "Author") return

    const contactAuthor = new ContactAuthorView({
      user_id: this.profileUserId,
      currentUserCanContactAuthors: this.currentUser.allowedToContactAuthors(),
      contactable: this.contactable,
      availableFor: this.availableFor,
      el: this.profileActionsElement,
    })

    this.profileActionsElement.append(contactAuthor.el)
  }

  renderDashboardButton() {
    // Readers don't have a dashboard
    if (this.profileUserType === "Reader") return

    // Authors can see their own dashboard, editors can see any
    if (!this.viewingOwnProfile() && !this.currentUser.isEditor()) return

    const href =
      this.profileUserType === "Editor"
        ? `/editorial/dashboards/${this.profileUserId}`
        : `/profiles/${this.profileUserSlug}/dashboard`

    const button = this.createButton(href, "profile.button.visit_dashboard", "button large primary")
    this.profileActionsElement.append(button)
  }

  renderEditProfileButton() {
    if (!this.viewingOwnProfile() && !this.currentUser.isEditor()) return

    const button = this.createButton(
      `/profiles/${this.profileUserSlug}/edit`,
      "profile.button.edit_profile",
      "edit-profile-button button large primary",
    )
    this.profileActionsElement.append(button)
  }

  renderModerateCommentsButton() {
    if (!this.currentUser.isEditor()) return

    const button = this.createButton(
      `/editorial/comments?commenter_id=${this.profileUserId}&filter_region=all&time_frame=all`,
      "profile.button.moderate_comments",
    )
    this.profileActionsElement.append(button)
  }

  renderSendBriefButton() {
    if (!this.currentUser.isEditor()) return
    if (this.profileUserType !== "Author") return

    const button = this.createButton(
      `/editorial/drafts?author_id=${this.profileUserId}`,
      "profile.button.send_brief",
    )
    this.profileActionsElement.append(button)
  }

  renderBehaviourButton() {
    if (!this.currentUser.isEditor()) return

    const button = this.createButton(
      `/editorial/users/${this.profileUserId}/behaviour`,
      "profile.button.behaviour",
    )
    this.profileActionsElement.append(button)
  }

  renderCloseAccountButton() {
    if (!this.closeable) return
    if (!this.currentUser.isEditor()) return
    if (!this.currentUser.hasAccessTo("close_account")) return

    const button = this.createButton(
      `/profiles/${this.profileUserSlug}/close`,
      "profile.button.close_account",
    )
    this.profileActionsElement.append(button)
  }

  createReactComponent(Component, props) {
    const container = document.createElement("div")
    container.className = Component.name.toLowerCase()
    this.profileActionsElement.append(container)

    const component = React.createElement(Component, props)
    const root = createRoot(container)
    root.render(component)
  }

  renderRemoveAllCommentsButton() {
    if (!this.currentUser.isEditor()) return
    if (!this.currentUser.hasAccessTo("comment_moderation_remove_all_comments")) return

    this.createReactComponent(RemoveAllComments, {
      className: "button large",
      removeCommentsPath: `/editorial/users/${this.profileUserId}/remove_all_comments`,
      userName: this.profileUserFullName,
    })
  }

  async renderContactDetails() {
    if (!this.hasPublicPhones && !this.hasPublicEmails && !this.currentUser.isEditor()) return

    const list = this.$el.find(".profile-details")[0]
    if (!list) return

    const joinedItem = list.querySelector(".profile-joined")
    const insert = (html) =>
      joinedItem
        ? joinedItem.insertAdjacentHTML("beforebegin", html)
        : list.insertAdjacentHTML("beforeend", html)

    if (!isSessionActive()) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
      const signInPrompt = (icon, key) =>
        `<li><a href="/sign_in?return_to=${returnTo}"><i class="${icon}"></i> ${i18n.t(key)}</a></li>`

      if (this.hasPublicPhones)
        insert(signInPrompt("icon-phone", "profile.show.prompt.sign_in_for_phones"))
      if (this.hasPublicEmails)
        insert(signInPrompt("icon-email", "profile.show.prompt.sign_in_for_emails"))
      return
    }

    const url = `/profiles/${this.profileUserSlug}/contact_details`
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok) return

    let data
    try {
      data = await response.json()
    } catch {
      return
    }

    const { emails, media_emails, media_label, phones } = data

    const phoneItems = Object.entries(phones).flatMap(([type, numbers]) =>
      numbers.map(
        (num) => `<li><i class="icon-${escape(type).replace(/_/g, "-")}"></i> ${escape(num)}</li>`,
      ),
    )

    if (!phoneItems.length && !emails.length && !media_emails.length) return

    const items = [
      ...phoneItems,
      ...emails.map((addr) => `<li><i class="icon-email"></i> ${escape(addr)}</li>`),
      ...media_emails.map(
        (addr) =>
          `<li>${escape(media_label)}<br/><a href="mailto:${escape(addr)}"><i class="icon-email"></i> ${escape(addr)}</a></li>`,
      ),
    ]

    insert(items.join(""))
  }

  renderToggleLockUserButton() {
    if (!this.currentUser.isEditor()) return

    this.createReactComponent(ToggleLockUser, {
      buttonClass: "button large",
      locked: this.locked,
      userId: this.profileUserId,
      userName: this.profileUserFullName,
    })
  }
}
